/**
 * PROTOTYPE live-config loader -- THROWAWAY.
 *
 * The spread drawers used hardcoded defaults, so an untouched value (e.g. per-record
 * ceiling) did not match what the engine actually prices with, and "overriding" a value
 * that already showed its placeholder default silently did nothing. This loads the real
 * active configuration the Configuration page uses and maps each module's block onto the
 * drawer's field keys, so the drawers show the engine's true defaults and overrides
 * behave as expected. Covers the volume-driven modules (MT, NDT, Coating).
 */
import { useEffect, useState } from 'react';
import { configurationService } from '../../configuration/services/configurationService';
import { mergeModuleConfigs } from '../../configuration/components/moduleConfigDefaults';
import { ModuleConfigs } from '../../configuration/components/moduleConfigTypes';
import { MODULE_DEFAULTS } from './spreadConfigDefs';

export type LiveModule = 'mt' | 'ndt' | 'coating' | 'platform' | 'retention';
export const LIVE_CONFIG_MODULES: LiveModule[] = ['mt', 'ndt', 'coating', 'platform', 'retention'];
export type LiveDefaults = Partial<Record<LiveModule, Record<string, number>>>;

// Drawer fields hold display percents (75, 10, 98); the backend stores fractions.
const PERCENT_KEYS = new Set(['marginTarget', 'qaOverheadPct', 'autoExtractRate', 'nre.validationOverhead', 'nre.marginTarget']);
const CONFIG_BLOCK: Record<LiveModule, keyof ModuleConfigs> = {
  mt: 'material_config', ndt: 'ndt_config', coating: 'coating_config',
  platform: 'platform_config', retention: 'retention_config',
};
const NRE_PREFIX = 'nre.';
const DIFFICULTY_PREFIX = 'nre.difficultyMultipliers.';

/** Map a module's live config (its block + the shared NRE block) onto the drawer's field
 *  keys in display units. The drawer keys match the backend config field names exactly,
 *  so each key resolves from the module block, the NRE block, or the difficulty map. */
export function liveModuleDefaults(cfg: ModuleConfigs, module: LiveModule): Record<string, number> {
  const block = cfg[CONFIG_BLOCK[module]] as unknown as Record<string, number | undefined>;
  const nre = cfg.nre_config as unknown as Record<string, number | undefined> & { difficultyMultipliers?: Record<string, number> };
  const out: Record<string, number> = {};
  Object.keys(MODULE_DEFAULTS[module]).forEach((key) => {
    if (key === 'durationDiscountPct') return; // per-proposal override; the band applies by default
    let v: number | undefined;
    if (key.startsWith(DIFFICULTY_PREFIX)) v = nre.difficultyMultipliers?.[key.slice(DIFFICULTY_PREFIX.length)];
    else if (key.startsWith(NRE_PREFIX)) v = nre[key.slice(NRE_PREFIX.length)];
    else v = block[key];
    if (v == null) return;
    out[key] = PERCENT_KEYS.has(key) ? v * 100 : v;
  });
  return out;
}

/** MT defaults only (kept as a named helper for the unit test). */
export function liveMtDefaults(cfg: ModuleConfigs): Record<string, number> {
  return liveModuleDefaults(cfg, 'mt');
}

/** Fetch the active config once; returns each module's drawer defaults, or null until
 *  loaded (and on failure, so the drawers fall back to their hardcoded constants). */
export function useLiveDefaults(): LiveDefaults | null {
  const [defaults, setDefaults] = useState<LiveDefaults | null>(null);
  useEffect(() => {
    let cancelled = false;
    configurationService.getCurrentConfiguration()
      .then((cfg) => {
        if (cancelled) return;
        const merged = mergeModuleConfigs(cfg);
        setDefaults(Object.fromEntries(LIVE_CONFIG_MODULES.map((m) => [m, liveModuleDefaults(merged, m)])) as LiveDefaults);
      })
      .catch(() => { /* keep hardcoded defaults */ });
    return () => { cancelled = true; };
  }, []);
  return defaults;
}
